import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUDIO_UPLOAD, type ContentLanguageCode } from '@/shared/constants';
import type { Work } from '@/shared/types';
import { getFirebaseImageUrl } from '@/shared/utils/imageUtils';
import { logger } from '@/shared/utils/logger';
import type { AudioDescriptionInput } from '../utils/audioDescription';
import { getAudioFileErrorKey } from '../utils/audioFileValidation';

type ByLanguage<T> = Record<ContentLanguageCode, T>;

interface UseAudioDescriptionFormParams {
  isOpen: boolean;
  work?: Work | null;
  languages: ContentLanguageCode[];
  currentLanguage: ContentLanguageCode;
  markDirty: () => void;
}

/** État de la section « Audiodescription de l'œuvre » de la modale épisode (D-009). */
export function useAudioDescriptionForm({
  isOpen,
  work,
  languages,
  currentLanguage,
  markDirty,
}: UseAudioDescriptionFormParams) {
  const { t } = useTranslation();
  const [files, setFiles] = useState({} as ByLanguage<File | null>);
  const [previews, setPreviews] = useState({} as ByLanguage<string | null>);
  const [transcription, setTranscription] = useState({} as ByLanguage<string>);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setPreviews((current) => {
      Object.values(current).forEach((url) => url?.startsWith('blob:') && URL.revokeObjectURL(url));
      return {} as ByLanguage<string | null>;
    });
    setFiles({} as ByLanguage<File | null>);
    setTranscription({} as ByLanguage<string>);
    setUploadError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    reset();
    if (!work) return;
    setTranscription((work.audioDescriptionTranscription || {}) as ByLanguage<string>);
    const urls = work.audioDescriptionUrl || {};
    let cancelled = false;
    (async () => {
      const loaded = {} as ByLanguage<string | null>;
      for (const lang of languages) {
        if (!urls[lang]) continue;
        try {
          loaded[lang] = await getFirebaseImageUrl(urls[lang]);
        } catch (error) {
          logger.error(`Erreur lors du chargement de l'audiodescription pour ${lang}:`, error);
        }
      }
      if (!cancelled) setPreviews(loaded);
    })();
    return () => {
      cancelled = true;
    };
    // Rechargé à l'ouverture ou au changement d'œuvre seulement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, work]);

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const errorKey = getAudioFileErrorKey(file);
    if (errorKey) {
      setUploadError(
        errorKey === 'format'
          ? t('exhibitions.audioUploadFormatError')
          : t('exhibitions.audioUploadSizeError', { maxSize: AUDIO_UPLOAD.MAX_SIZE_MB }),
      );
      return;
    }
    setUploadError(null);
    markDirty();
    setFiles((current) => ({ ...current, [currentLanguage]: file }));
    setPreviews((current) => ({ ...current, [currentLanguage]: URL.createObjectURL(file) }));
  };

  const handleRemove = () => {
    setUploadError(null);
    markDirty();
    const preview = previews[currentLanguage];
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    setFiles((current) => ({ ...current, [currentLanguage]: null }));
    setPreviews((current) => ({ ...current, [currentLanguage]: null }));
  };

  const setTranscriptionForCurrentLanguage = (value: string) => {
    markDirty();
    setTranscription((current) => ({ ...current, [currentLanguage]: value }));
  };

  const hasAudio = Object.fromEntries(
    languages.map((lang) => [lang, Boolean(previews[lang])]),
  ) as ByLanguage<boolean>;

  /** Données à passer à `saveWork`. */
  const toSaveInput = (): AudioDescriptionInput => ({
    files,
    kept: Object.fromEntries(
      languages.map((lang) => [lang, Boolean(previews[lang]) && !files[lang]]),
    ) as ByLanguage<boolean>,
    transcription,
  });

  return {
    inputRef,
    preview: previews[currentLanguage] ?? null,
    transcription: transcription[currentLanguage] || '',
    allTranscriptions: transcription,
    hasAudio,
    uploadError,
    handleSelect,
    handleRemove,
    openFilePicker: () => inputRef.current?.click(),
    setTranscription: setTranscriptionForCurrentLanguage,
    toSaveInput,
    reset,
  };
}

export type AudioDescriptionForm = ReturnType<typeof useAudioDescriptionForm>;
